<template>
  <form id="booking-info" @submit.prevent="submit">
    <b-row>
      <b-col sm="12" md="6">
        <b-card :title="$t('bookings.route')">
          <fields id="route" group="route" />
          <div v-if="booking.created_at" class="form-group row">
            <label class="col-4 col-form-label text-right">
              {{ $t('bookings.created') }}
            </label>
            <div class="col-8 col-form-label">
              {{ booking.created_at | moment($dateFormat) }}
            </div>
          </div>
        </b-card>
        <div
          v-if="booking"
          ref="mapWrapper"
          class="mb-4 embed-responsive embed-responsive-16by9"
        >
          <div
            id="booking-map"
            ref="map"
            :class="{ blurred: !showFullAddress }"
            class="embed-responsive-item rounded"
          >
            Map is loading ...
          </div>
        </div>
        <b-card ref="estimation" :title="$t('bookings.estimation')">
          <b-row v-if="vehicle" class="mb-4">
            <b-col cols="4" class="text-right">
              {{ $t('bookings.vehicle_class') }}
            </b-col>
            <b-col cols="8">
              <BookingVehicle
                :vehicle="vehicle"
                :sharing-vehicle="sharingVehicle"
              />
            </b-col>
          </b-row>
          <div v-if="booking.calculated" class="form-group row">
            <label class="col-4 col-form-label text-right">
              {{ $t('bookings.distance') }}
            </label>
            <div class="col-8 col-form-label">
              {{ booking.calculated.distance | distance }}
            </div>
          </div>
          <div v-if="booking.calculated" class="form-group row">
            <label class="col-4 col-form-label text-right">
              {{ $t('bookings.time') }}
            </label>
            <div class="col-8 col-form-label">
              {{ booking.calculated.time | humanizeDuration() }}
            </div>
          </div>
          <b-row v-if="booking.calculated" class="mb-4">
            <b-col cols="4" class="text-right">
              {{ $t('bookings.estimation') }}
            </b-col>
            <b-col cols="8">
              <booking-amount :booking="booking" :show-fees="false" />
            </b-col>
          </b-row>
        </b-card>
        <price-offer-form
          v-if="showPriceOfferForm"
          :currency-label="booking.calculated.currency_label"
          :companies="priceOfferCompanies"
          @send="onSendPrice"
        />
        <price-offer-company
          v-if="priceOfferCompany.price"
          :currency-label="booking.calculated.currency_label"
          :offer="priceOfferCompany"
        />

        <invoices
          :invoices="invoices"
          :show-url="$allows('administer')"
          :show-affiliate-commission="$allows('view affiliate commission')"
          :show-partner-commission="$allows('view partner commission')"
        />

        <cancel-reason
          v-if="model.status === 'cancelled'"
          :id="booking.cancel_reason_id"
          :text="booking.cancel_reason_text"
        />

        <price-offers
          v-if="$allows('administer')"
          :offers="priceOffers"
          :show-system-info="$allows('administer')"
          @accept="onPriceOfferAccept"
        />

        <RejectedAssignments
          v-if="$allows('administer')"
          :assignments="rejectedAssignments"
        />

        <b-card v-if="canEditAssignments" :title="$t('bookings.assignments')">
          <fields group="assigning" />
          <div v-if="showChangeCompanyButton" class="text-right">
            <button
              type="button"
              class="btn btn-primary"
              @click="changeCompany"
            >
              Change company
            </button>
          </div>
          <div v-else-if="showUpdateCompanyDataButton" class="text-right">
            <button
              type="button"
              class="btn btn-primary"
              @click="updateCompanyData"
            >
              {{ $t('general.save') }}
            </button>
          </div>
        </b-card>

        <BookingSuitableCompanies
          v-if="suitableCompanies.length > 0"
          :companies="suitableCompanies"
          @assign="assign"
        />
      </b-col>
      <b-col sm="12" md="6">
        <b-card id="additional_info" :title="$t('bookings.additional_info')">
          <fields v-if="model.ride_type !== 'delivery'" ungrouped />

          <fields
            v-if="model.ride_type === 'delivery'"
            group="delivery_details"
          />
          <fields
            v-if="model.details && model.details.door_to_door"
            group="door_to_door_details"
          />
        </b-card>
        <b-card v-if="canViewClientInfo" :title="$t('bookings.client_info')">
          <fields group="client" />
          <p>
            <i class="icon-exclamation text-info text-lg"></i>
            {{ $t('bookings.messages.client_updating') }}
          </p>
        </b-card>
        <b-card
          v-if="jobStatusHistory.length > 0"
          :title="$t('bookings.job_status_history')"
        >
          <JobStatusHistory :rows="jobStatusHistory"></JobStatusHistory>
        </b-card>
      </b-col>
    </b-row>
    <b-card>
      <b-col class="actions flex justify-between">
        <div class="flex action-items">
          <b-button
            :disabled="!enableSaveButton"
            type="submit"
            variant="primary"
          >
            {{ $t('general.save') }}
          </b-button>
          <b-button type="button" variant="light" class="ml-2" @click="cancel">
            {{ $t('general.back') }}
          </b-button>
        </div>
        <div class="flex action-items">
          <b-button
            v-if="canAccept"
            type="button"
            variant="success"
            class="ml-2"
            @click="acceptBooking"
          >
            <i class="icon-check"></i>
            {{ $t('bookings.accept') }}
          </b-button>
          <b-button
            v-if="canReject"
            type="button"
            variant="warning"
            class="ml-2"
            @click="reject"
          >
            <i class="icon-blocked"></i>
            {{ $t('bookings.reject') }}
          </b-button>
          <booking-cancel-btn
            v-if="canCancel"
            :button-text="cancelButtonText"
            :is-main="!booking.is_return"
            @click="cancelBooking"
          />
          <b-button
            v-if="$allows('bookings.delete')"
            type="button"
            variant="danger"
            class="ml-2"
            @click="deleteBooking"
          >
            <i class="icon-trash"></i>
            {{ $t('general.delete') }}
          </b-button>
          <b-button
            v-if="canBlock"
            type="button"
            variant="danger"
            class="ml-2"
            @click.prevent="showBlockModal"
          >
            <i class="icon-blocked"></i>
            {{ $t('bookings.block') }}
          </b-button>
        </div>
      </b-col>
    </b-card>
    <ActionModal
      v-if="canCancel"
      id="cancel-modal"
      :loading="modalBusy"
      :title="$t('bookings.messages.confirm_cancelling')"
      :reasons="cancelReasons"
      @confirm="onConfirmCancel"
    />
    <ActionModal
      v-if="canReject"
      id="reject-modal"
      :reasons="rejectReasons"
      :loading="modalBusy"
      :title="$t('bookings.reject')"
      @confirm="onConfirmReject"
    />
    <ActionModal
      v-if="canBlock"
      id="block-modal"
      :loading="modalBusy"
      :title="$t('bookings.block')"
      custom-reason
      @confirm="onConfirmBlock"
    />
  </form>
</template>

<script>
import debounce from 'lodash-es/debounce'
import pick from 'lodash-es/pick'
import { ResourceFormMixin } from 'stylemix-vue-admin'
import BookingsApi from './Api'
import { loadGoogleMaps } from 'stylemix-base'
import polyline from '@mapbox/polyline'
import humanizeDuration from 'humanize-duration'

import BookingAmount from './BookingAmount'
import Invoices from './Invoices'
import { getAddressDetailsFromGeocode } from './MapUtils'
import BookingCancelBtn from './BookingCancelBtn'
import CancelReason from './CancelReason'
import PriceOfferCompany from './PriceOfferCompany'
import PriceOfferForm from './PriceOfferForm'
import PriceOffers from './PriceOffers'
import BookingVehicle from '../components/BookingVehicle'
import { mapState } from 'vuex'
import ActionModal from './ActionModal'
import RejectedAssignments from './RejectedAssignments'
import JobStatusHistory from './JobStatusHistory'
import Swal from "sweetalert2";
import BookingSuitableCompanies from './BookingSuitableCompanies.vue'

const calcProps = [
    'vehicle_id',
    'origin_id',
    'destination_id',
    'route',
    'details.passengers',
    'details.baby_seats',
    'details.booster_seats',
    'details.meet_greet',
    'details.ski_equipment',
    'details.disabled_place',
    'details.animals',
    'details.door_to_door',
    'details.loaders',
    'details.luggage',
    'details.waiting_time',
  ],
  routeProps = ['route', 'origin_id', 'destination_id']

export default {
  name: 'BookingForm',
  components: {
    ActionModal,
    BookingAmount,
    BookingCancelBtn,
    BookingSuitableCompanies,
    BookingVehicle,
    CancelReason,
    Invoices,
    JobStatusHistory,
    PriceOffers,
    PriceOfferCompany,
    PriceOfferForm,
    RejectedAssignments,
  },
  filters: {
    distance(value) {
      return (value / 1000).toFixed(2).toString() + 'km'
    },
    humanizeDuration(value) {
      return humanizeDuration(value * 1000, { units: ['h', 'm'], round: true })
    },
  },

  mixins: [ResourceFormMixin],

  data() {
    return {
      enableSaveButton: false,
      modelChanged: false,
      base: 'bookings',
      cancellable: false,

      // Booking resource itself
      // Required for displaying data that available only in view context
      booking: {},

      // Return booking if current is main booking
      returnBooking: null,

      // Main booking if current is return booking
      mainBooking: null,

      // Vehicle for booking
      vehicle: null,
      sharingVehicle: null,

      // Company assigned to booking
      company: null,

      // Driver assigned to booking
      driver: null,

      // Order model for booking
      order: null,
      invoiceUrl: null,
      lastPayment: null,
      invoices: [],
      priceChanged: false,
      oldPrice: 0,
      priceOffers: [],
      priceOfferCompanies: [],
      priceOfferCompany: {},
      cancelReasons: [],
      cancelWithMain: false,
      allowedMethods: [],
      showFullAddress: true,
      suitableCompanies: [],
      rejectReasons: [],
      rejectedAssignments: [],
      jobStatusHistory: [],
      modalBusy: false,
    }
  },

  computed: {
    ...mapState('admin-auth', ['account']),
    canCancel() {
      return this.cancellable
    },
    canAccept() {
      return this.model && this.model.calculated && this.model.calculated.total
        ? this.model.status === 'assigned'
        : false
    },
    canReject() {
      return this.model ? this.model.status === 'assigned' : false
    },
    priceOfferRequired() {
      return this.model && this.model.calculated && !this.model.calculated.total
    },
    canEditAssignments() {
      return this.model
        ? !this.priceOfferRequired &&
            (['assigned', 'processing'].indexOf(this.model.status) >= 0 ||
              this.canEditCompany)
        : false
    },
    canEditCompany() {
      return !!(
        this.$allows('administer') || this.$allows('bookings.change_company')
      )
    },
    canViewClientInfo() {
      return (
        this.$allows('administer') ||
        (this.$account.company &&
          (this.$account.company.id === this.model.company_id || this.$allows('administer')))
      )
    },
    orderStatusLabel() {
      return this.order
        ? this.order.status_label
        : this.$t('enums.order_status.pending')
    },
    isOnInvoice() {
      return this.order === null
    },
    showPriceOfferForm() {
      return (
        this.$allows &&
        this.$allows('bookings.serve') &&
        this.priceOfferRequired &&
        !this.priceOfferCompany.price
      )
    },
    showChangeCompanyButton() {
      return (
        this.model.company_id &&
        ['processing', 'complete'].indexOf(this.model.status) >= 0 &&
        this.canEditCompany
      )
    },
    showUpdateCompanyDataButton() {
      return (
        this.model.company_id &&
        this.account.company &&
        (this.account.company.id === this.model.company_id || this.$allows('administer')) &&
        ['processing', 'complete'].includes(this.model.status)
      )
    },
    cancelButtonText() {
      return this.$t(
        this.returnBooking ? 'bookings.cancel_with_return' : 'bookings.cancel'
      )
    },
    canBlock() {
      return this.$allows('bookings.block') && this.model.is_blocked === false
    },
  },

  watch: {
    model: {
      handler() {
        // ignore first change
        if (!this.modelChanged) {
          this.modelChanged = true
          return
        }

        this.enableSaveButton = true
      },
      deep: true,
    },
    'model.company_id': {
      handler: function (val, old) {
        if (old !== undefined && val != old) {
          this.model.driver_id = null
          const driverId = this.fields.get('driver_id')
          this.fields.get('driver_id').options = []

          if (val) {
            // watcher ignore changing this key
            driverId.source.params = {
              ...driverId.source.params,
              'where[company_id]': val,
            }
            driverId.disabled = false
          } else {
            driverId.disabled = true
          }
        }
      },
    },
    priceChanged() {
      if (this.priceChanged) {
        this.$toast.info(this.$t('bookings.messages.price_changed'))
      }
    },
    'model.driver_id': {
      handler: function (val) {
        if (!val || !this.$allows) {
          return
        }

        this.api()
          .getDriver(val)
          .then((response) => {
            if (!this.model.details.vehicle_model) {
              this.model.details.vehicle_model = response.data.vehicle_model
            }
            if (!this.model.details.vehicle_color) {
              this.model.details.vehicle_color = response.data.vehicle_color
            }
            if (!this.model.details.vehicle_number) {
              this.model.details.vehicle_number = response.data.vehicle_number
            }
          })
      },
    },
    'model.details.meet_greet': {
      handler(newVal) {
        this.updateMeetGreetFields(newVal)
      },
    },
  },

  mounted() {
    this.loadForm().then((result) => {
      this.booking = result.booking
      this.returnBooking = result.return_booking
      this.mainBooking = result.main_booking
      this.cancellable = result.cancellable
      this.editable = result.editable
      this.vehicle = result.vehicle
      this.sharingVehicle = result.sharing_vehicle
      this.company = result.company
      this.driver = result.driver
      this.order = result.order
      if (this.order) {
        this.order.amount_left = this.order.amount - this.order.amount_paid
      }
      this.invoiceUrl = result.invoice_url
      this.lastPayment = result.payments ? result.payments[0] : null
      this.invoices = this.prepareInvoices(result.invoices)
      this.oldPrice = this.booking.calculated
        ? this.booking.calculated.total
        : 0
      this.priceOffers = result.price_offers || []
      this.priceOfferCompanies = result.price_offer_companies || []
      this.priceOfferCompany = result.price_offer || {}
      this.cancelReasons = result.cancel_reasons || []
      this.allowedMethods = result.allowed_methods || []
      this.showFullAddress = result.show_full_address !== false
      this.suitableCompanies = result.suitable_companies || []
      this.rejectReasons = result.reject_reasons || []
      this.rejectedAssignments = result.rejected_assignments || []
      this.jobStatusHistory = result.job_status_history || []

      this.$emit('loaded', result)

      this.setFieldGroup('route', [
        'pickup_date',
        'finished_at',
        'route.origin',
        'route.destination',
        'origin_id',
        'destination_id',
        'route.stops',
      ])

      this.setFieldGroup('assigning', [
        'company',
        'company_id',
        'driver_id',
        'details.vehicle_model',
        'details.vehicle_color',
        'details.vehicle_number',
        'details.company_notes',
      ])

      this.setFieldGroup('client', [
        'owner_id',
        'details.client_appeal',
        'details.client_first_name',
        'details.client_last_name',
        'details.client_gender',
        'details.client_email',
        'details.client_email_2',
        'details.client_phone',
        'details.client_phone_2',
      ])

      this.setFieldGroup('delivery_details', [
        'details.recipient.name',
        'details.recipient.phone',
        'details.loaders',
        'details.door_to_door',
      ])

      this.setFieldGroup('door_to_door_details', [
        'details.door_to_door_details.entrance',
        'details.door_to_door_details.intercom',
        'details.door_to_door_details.floor',
        'details.door_to_door_details.apartment',
      ])

      this.initMap()
      this.watchModel()

      if (this.$route.params.action === 'reject') {
        this.$nextTick(() => {
          this.reject()
        })
      }
      this.$nextTick(() => {
        this.updateMeetGreetFields(this.model.details.meet_greet)
      })
    })
  },

  methods: {
    api() {
      return new BookingsApi({
        division_id: this.currentDivision && this.currentDivision.id,
        vehicle_id: this.model && this.model.vehicle_id,
        ride_type: this.model && this.model.ride_type,
        partner_id: this.model && this.model.partner_id
      })
    },

    submit() {
      if (
        this.priceChanged &&
        this.booking.order &&
        this.booking.order.payment_method === 'cash'
      ) {
        // const price = this.$n(this.booking.calculated.total, {
        //   style: 'currency',
        //   currency: this.booking.calculated.currency,
        // })
        const price = this.booking.calculated.currency.code.value + ' ' + this.booking.calculated.total
        const diff = Number(
          this.booking.calculated.total - this.oldPrice
        ).toFixed(2)
        this.$alert.warning(
          this.$t('bookings.messages.price_changed'),
          this.$t('bookings.messages.cash_confirm_alert_text', { price, diff }),
          {
            showCancelButton: true,
            preConfirm: () => {
              this.save()
            },
          }
        )
        return
      }
      this.save()
    },

    initMap() {
      loadGoogleMaps(['places']).then(() => {
        const google = window.google

        this.map = new google.maps.Map(this.$refs.map, {
          zoom: 3,
          center: { lat: 0, lng: 0 },
        })

        this.polylinePoints = polyline
          .decode(this.booking.polyline)
          .map(([lat, lng]) => {
            return { lat, lng }
          })

        this.drawOnMap()
      })
    },

    drawOnMap() {
      const google = window.google
      let bounds = new google.maps.LatLngBounds()
      let route = this.polylinePoints
      route.forEach((point) => {
        bounds.extend(point)
      })
      this.map.fitBounds(bounds)

      if (this.mapElements && this.mapElements.length) {
        this.mapElements.forEach((element) => element.setMap(null))
      }

      this.mapElements = [
        new google.maps.Marker({
          position: route[0],
          map: this.map,
          label: {
            text: 'A',
            color: 'white',
          },
          draggable:
            this.fields.byAttribute['route.origin'] &&
            this.fields.byAttribute['route.origin'].disabled !== true &&
            this.booking.origin_id === null,
        }),
        new google.maps.Marker({
          position: route[route.length - 1],
          map: this.map,
          label: {
            text: 'B',
            color: 'white',
          },
          draggable:
            this.fields.byAttribute['route.destination'] &&
            this.fields.byAttribute['route.destination'].disabled !== true &&
            this.booking.destination_id === null,
        }),
        new google.maps.Polyline({
          path: route,
          map: this.map,
          geodesic: true,
          strokeColor: '#FF0000',
          strokeOpacity: 1.0,
          strokeWeight: 2,
        }),
      ]
      google.maps.event.addListener(this.mapElements[0], 'dragend', () =>
        this.markerDragEnd(this.mapElements[0], 'origin')
      )
      google.maps.event.addListener(this.mapElements[1], 'dragend', () =>
        this.markerDragEnd(this.mapElements[1], 'destination')
      )
    },

    async acceptBooking() {
      if (!this.model.driver_id) {
        this.$alert.warning(this.$t('bookings.messages.assign_driver'))
        return
      }

      if (
        !(
          await this.$alert.question(
            this.$t('bookings.messages.confirm_accept')
          )
        ).value
      ) {
        return
      }

      const company = this.fields.byAttribute.company_id
        ? this.model.company_id
        : null

      const promise = new BookingsApi()
        .accept(this.model, company)
        .then((result) => {
          if(result.error) {
            Swal.fire({
              type: "warning",
              title: this.$t('company.commissions.has_unpaid_order_invoice'),
              text: "",
              showCancelButton: true,
              confirmButtonText: this.$t('general.pay'),
              confirmButtonColor: "#4caf50",
              preConfirm: () => {
                this.$router.push({
                  name: 'company-orders-pay',
                  params: {
                    id: result.data.order_id,
                  },
                })
              }
            });
          } else {
            this.model.status = result.data.status
            this.booking.status = result.data.status
            this.$toast.success(result.message)
            this.$emit('assigned')
          }
        })

      this.$uiBlocker(promise, this.$el)
    },

    reject() {
      if (!this.canReject) {
        return
      }
      this.$bvModal.show('reject-modal')
    },

    onConfirmReject({ reason, comment }) {
      if (!this.canReject) {
        return
      }
      const promise = new BookingsApi()
        .reject(this.model.id, this.model.company_id, comment || reason)
        .then((result) => {
          this.model.status = result.data.status
          this.booking.status = result.data.status
          this.$toast.success(result.message)
          this.$emit('rejected')
        })
      this.$uiBlocker(promise, this.$el)
    },

    async deleteBooking() {
      if (
        !(
          await this.$alert.warning(
            this.$t('bookings.messages.confirm_deleting')
          )
        ).value
      ) {
        return
      }

      const promise = new BookingsApi()
        .destroy(this.model.id)
        .then((result) => {
          this.$toast.success(result.message)
          this.cancel()
        })

      this.$uiBlocker(promise, this.$el)
    },

    cancelBooking(with_main) {
      this.cancelWithMain = with_main
      this.$bvModal.show('cancel-modal')
    },

    onConfirmCancel({ reason, comment }) {
      const promise = new BookingsApi().cancel(this.model.id, {
        with_main: this.cancelWithMain,
        reason_id: reason,
        reason_text: comment,
      })

      return promise.then((result) => {
        this.$toast.success(result.message)
        this.cancel()
        this.$bvModal.hide('cancel-modal')
      })
    },

    watchModel() {
      calcProps.forEach((prop) => {
        this.$watch(`model.${prop}`, debounce(this.validateVehicle, 800), {
          deep: true,
        })
      })
      routeProps.forEach((prop) => {
        this.$watch(`model.${prop}`, debounce(this.resolveRoute, 800), {
          deep: true,
        })
      })
    },

    resolveRoute() {
      const payload = pick(this.model, routeProps)

      const promise = this.$http
        .post('booking-process/route', payload)
        .then((response) => {
          this.polylinePoints = response.data.route_info.polyline_points.map(
            ([lat, lng]) => ({ lat, lng })
          )
          this.drawOnMap()
        })

      this.$uiBlocker(promise, this.$refs.mapWrapper)
    },

    calculate() {
      const payload = pick(this.model, calcProps)
      if (this.model.is_return) {
        payload.is_return = true
      }
      payload.ride_type = this.model.ride_type
      payload.partner_id = this.model.partner_id

      const promise = this.$http
        .post('booking-process/calculate', payload)
        .then((response) => {
          this.priceChanged =
            parseFloat(this.booking.calculated.total) !==
            parseFloat(response.data.calculated_ride.total)
          Object.assign(this.booking.calculated, response.data.calculated_ride)
        })

      this.$uiBlocker(promise, this.$refs.estimation)
    },

    validateVehicle() {
      const payload = pick(this.model, calcProps)
      payload.pickup_date = this.model.pickup_date
      payload.ride_type = this.model.ride_type
      this.errors.clear()
      const promise = this.$http
        .post('booking-process/validate-vehicle', payload)
        .then(() => {
          this.calculate()
        })
        .catch((error) => {
          if (error.response.data && error.response.data.errors) {
            this.setValidationErrors(error.response.data.errors)
          }
          this.$alert.warning(
            this.$t('bookings.messages.current_vehicle_not_suitable'),
            this.$t('bookings.messages.change_vehicle_alert_text'),
            {
              showCancelButton: true,
              preConfirm: (changeVehicle) => {
                if (changeVehicle) {
                  this.changeVehicle(payload)
                }
              },
            }
          )
        })

      this.$uiBlocker(promise, this.$refs.estimation)
    },

    changeVehicle(payload) {
      this.$http
        .post('booking-process/vehicles', payload)
        .then((response) => {
          this.vehicle = response.data.data.sort((v1, v2) =>
            v1.order <= v2.order ? -1 : 1
          )[0]
          this.model.vehicle_id = this.vehicle.id
          this.$toast.success(
            this.$t('bookings.messages.vehicle_class_has_been_changed')
          )
          this.calculate()
        })
        .catch((error) => {
          if (error && error.response.data && error.response.data.errors) {
            this.setValidationErrors(error.response.data.errors)
          }
        })
    },

    offerPrice() {},

    markerDragEnd(marker, point) {
      const google = window.google
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ location: marker.position }, (results, status) => {
        if (status === 'OK') {
          this.model.route[point] = getAddressDetailsFromGeocode(results[0])
        }
      })
    },

    async onPriceOfferAccept(offerId) {
      if (
        !(
          await this.$alert.question(
            this.$t('bookings.messages.confirm_price_offer_accept')
          )
        ).value
      ) {
        return
      }
      const promise = new BookingsApi()
        .acceptPriceOffer(offerId)
        .then((result) => {
          this.$toast.success(result.message)
          this.$emit('price_offer_accepted')
        })
        .then(() => {
          return this.api().edit(this.model.id)
        })
        .then((result) => {
          this.$router.push(this.acceptPriceOfferRedirectRoute(result))
        })
      this.$uiBlocker(promise, this.$el)
    },

    async onSendPrice(data) {
      const promise = new BookingsApi().sendPrice(this.model.id, data)

      promise.then((result) => {
        this.$toast.success(result.message)
        this.$emit('price_offer_sent')
      })
      this.$uiBlocker(promise, this.$el)
    },

    changeCompany() {
      const api = new BookingsApi()
      const promise = api.changeCompany(this.model.id, {
        company_id: this.model.company_id,
        driver_id: this.model.driver_id,
        vehicle_model: this.model.details.vehicle_model,
        vehicle_color: this.model.details.vehicle_color,
        vehicle_number: this.model.details.vehicle_number,
        company_notes: this.model.details.company_notes,
      })

      promise.then((result) => {
        this.$toast.success(result.message)
        this.$emit('company_changed')
      })
      this.$uiBlocker(promise, this.$el)
    },

    updateCompanyData() {
      const api = new BookingsApi()
      const promise = api.updateCompanyData(this.model.id, {
        driver_id: this.model.driver_id,
        vehicle_model: this.model.details.vehicle_model,
        vehicle_color: this.model.details.vehicle_color,
        vehicle_number: this.model.details.vehicle_number,
        company_notes: this.model.details.company_notes,
      })

      promise.then((result) => {
        this.$toast.success(result.message)
        this.$emit('company_changed')
      })
      this.$uiBlocker(promise, this.$el)
    },

    acceptPriceOfferRedirectRoute() {
      return {
        name: 'bookings',
      }
    },

    assign(companyId) {
      return this.$uiBlocker(
        new BookingsApi()
          .assign(this.booking, { company_id: companyId })
          .then((result) => {
            this.$toast.success(result.message)
            this.$router.go(0)
          })
          .catch((error) => {
            if (error.status === 422) {
              this.$toast.error(error.data.message)
            }
          }),
        this.$el
      )
    },

    showBlockModal() {
      this.$bvModal.show('block-modal')
    },

    onConfirmBlock({ reason }) {
      this.modalBusy = true
      this.api()
        .block(this.model.id, reason)
        .then((result) => {
          this.modalBusy = false
          this.$toast.success(result.message)
          this.$router.push({ name: 'bookings' })
        })
    },

    prepareInvoices(invoices) {
      if (invoices && invoices.length > 0) {
        return invoices
      }

      if (this.booking.order_id) {
        return []
      }

      return [
        {
          payment_method: 'on_invoice',
          number: '#',
          subtotal: this.booking.calculated.total,
          currency: this.booking.calculated.currency,
          currency_label: this.booking.calculated.currency_label,
        },
      ]
    },

    updateMeetGreetFields(newVal) {
      if (this.fields.byAttribute['details.meet_text']) {
        this.fields.byAttribute['details.meet_text'].disabled = !newVal
        if (document.querySelector('[attribute="details.meet_text"]')) {
          document.querySelector('[attribute="details.meet_text"]').hidden =
            !newVal
        }
      }
      if (this.fields.byAttribute['details.meet_photo']) {
        this.fields.byAttribute['details.meet_photo'].disabled = !newVal
        if (document.querySelector('[attribute="details.meet_photo"]')) {
          document.querySelector('[attribute="details.meet_photo"]').hidden =
            !newVal
        }
      }
    },
  },
}
</script>

<style lang="scss">
#booking-info {
  #route {
    .col-sm-4 {
      max-width: 33.33333%;
      text-align: right !important;
    }

    .col-sm-8 {
      max-width: 66.66667%;
    }
  }

  .blurred {
    filter: blur(5px);
  }

  .actions {
    button:not(:last-child) {
    }
  }
}

@media (max-width: 600px) {
  #booking-info {
    .actions {
      flex-direction: column;

      .action-items {
        flex-wrap: wrap;
        justify-content: center;

        button {
          width: 48%;
          margin: 1% !important;
        }
      }
    }
  }
}
</style>
